import { ReactElement, FC } from 'react'

import { Switch } from '@headlessui/react'
import { clsx } from 'clsx'

import { useBackgroundContext } from '../BackgroundContext/index.js'
import { HTMLTribeProps } from '../types/index.js'

export type ToggleProps = Omit<HTMLTribeProps<'input'>, 'title' | 'size'> & {
  checked: boolean
  onChange: (value: boolean) => void
  title?: string | ReactElement
  name?: string
  description?: string | ReactElement
  size?: 'sm' | 'md'
}
/**
 * The Toggle component is used as an alternative for the Checkbox component. You can switch between enabled or disabled states.
 */
export const Toggle: FC<ToggleProps> = props => {
  const {
    title,
    description,
    size = 'md',
    checked,
    disabled = false,
    onChange,
    className,
  } = props
  const { text500Clsx, backgroundType } = useBackgroundContext()

  const text700Clsx = [
    backgroundType === 'main' && 'text-content-on-background',
    backgroundType === 'surface' && 'text-content',
    backgroundType === 'secondary' && 'text-content-on-topbar',
  ]

  const sizeClsx = [size === 'sm' && 'h-4 w-7', size === 'md' && 'h-6 w-11']
  const circleSizeClsx = [
    size === 'sm' && 'h-3 w-3',
    size === 'md' && 'h-5 w-5',
  ]
  const translateClsx = [
    size === 'sm' && 'translate-x-3 rtl:-translate-x-3',
    size === 'md' && 'translate-x-5 rtl:-translate-x-5',
  ]

  const switchClsx = [
    checked ? 'bg-action-primary' : 'bg-surface-pressed',
    'relative inline-flex flex-shrink-0 border-2 border-transparent rounded-full transition-colors ease-in-out duration-200',
    sizeClsx,
    !disabled && 'cursor-pointer',
    disabled && 'opacity-50 cursor-default',
    'focus:outline-none focus-visible:ring',
  ]

  if (title || description) {
    return (
      <Switch.Group
        as="div"
        className={clsx('flex items-top justify-between space-s-2', className)}
      >
        <span className="flex-grow flex flex-col">
          <Switch.Label
            as="span"
            className={clsx('font-medium', text700Clsx)}
            passive
          >
            {title}
          </Switch.Label>
          <Switch.Description
            as="span"
            className={clsx('text-sm', text500Clsx)}
          >
            {description}
          </Switch.Description>
        </span>
        <Switch
          checked={checked}
          onChange={onChange}
          className={clsx(switchClsx)}
          disabled={disabled}
        >
          <span
            aria-hidden="true"
            className={clsx(
              checked ? translateClsx : 'translate-x-0',
              'pointer-events-none inline-block h-5 w-5 rounded-full bg-surface shadow transform transition ease-in-out duration-200',
            )}
          />
        </Switch>
      </Switch.Group>
    )
  }

  return (
    <Switch
      checked={checked}
      onChange={onChange}
      className={clsx(switchClsx)}
      disabled={disabled}
    >
      <span className="sr-only">{title}</span>
      <span
        aria-hidden="true"
        className={clsx(
          checked ? translateClsx : 'translate-x-0',
          circleSizeClsx,
          'pointer-events-none inline-block rounded-full bg-surface shadow transform transition ease-in-out duration-200',
        )}
      />
    </Switch>
  )
}
